import React from "react";
import { Quote, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { PageHero } from "./PageHero";

const founder = {
  name: "Ben Hughes",
  role: "Founder & Director of Operations",
  photo: "/ben-headshot.jpg",
  objectPosition: "center 30%",
  bio: [
    "Ben Hughes is the Founder and Director of Operations of Robo Demo LLC. A Heavy Equipment Operations Supervisor with 18 years in the Army National Guard — where he still serves today — Ben brings military-grade discipline and precision to the demolition industry.",
    "Demolition is in Ben's blood. As a fourth-generation demolition professional, he grew up in the trade and learned it from the ground up. Ben has taken everything passed down through four generations of his family and combined it with modern strategies and new technology to build Robo Demo LLC — honoring that legacy while pushing the industry forward.",
    "He founded Robo Demo LLC in August 2023 as a service-disabled veteran-owned small business. Based in Virginia and serving the DMV area and nationwide, Ben has hands-on involvement in every project and selects the proper team for each job — holding every job to the standard he lives by.",
  ],
  quote:
    "I run every job like a mission — plan it, execute it safely, and get it done right the first time.",
};

export const Founders = () => {
  const navigate = useNavigate();

  return (
    <div data-testid="founders-page">
      <PageHero
        kicker="Meet The Founder"
        title="Veteran-Led,"
        highlight="Mission-Driven"
        subtitle="Robo Demo LLC is led by a veteran who brings military discipline and precision to every demolition project."
      />

      {/* Founder profile */}
      <section data-testid="founder-profile" className="tex-plate py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Headshot */}
          <div className="relative lg:sticky lg:top-28">
            <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-primary" />
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={founder.photo}
                alt={`${founder.name}, ${founder.role} of Robo Demo LLC`}
                className="w-full h-full object-cover"
                style={{ objectPosition: founder.objectPosition }}
                data-testid="founder-headshot"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background via-background/70 to-transparent p-6 pt-16">
                <h2 className="font-display text-3xl font-black uppercase tracking-tight text-white leading-none">
                  {founder.name}
                </h2>
                <p className="text-primary font-semibold uppercase tracking-[0.15em] text-sm mt-1">
                  {founder.role}
                </p>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-primary" />
          </div>

          {/* Bio */}
          <div>
            <p className="label-kicker mb-4">{founder.role}</p>
            <h3 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none mb-6">
              {founder.name}
            </h3>
            <div className="space-y-5 text-gray-400 leading-relaxed">
              {founder.bio.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <figure className="mt-8 border-l-2 border-primary pl-6 py-2">
              <Quote className="h-6 w-6 text-primary mb-3" />
              <blockquote className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight text-white leading-tight">
                {founder.quote}
              </blockquote>
              <figcaption className="mt-3 text-sm uppercase tracking-[0.2em] text-muted-foreground">
                — {founder.name}, {founder.role}
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 border-t border-border overflow-hidden">
        <div className="absolute inset-0 hazard-stripe opacity-[0.04]" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
            Work With A Veteran You Can Trust
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            Partner with a leader who holds every project to the highest standard
            of safety and precision.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="founders-quote-btn" size="lg" onClick={() => navigate("/quote")} className="rounded-none font-bold uppercase tracking-widest px-8 py-6">
              Get Your Free Quote <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button data-testid="founders-services-btn" size="lg" variant="outline" onClick={() => navigate("/services")} className="rounded-none font-bold uppercase tracking-widest px-8 py-6 border-white/30 bg-transparent text-white hover:bg-white hover:text-background">
              View Our Services
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
